// src/render/sprites/character-sprite.ts — D-01 top-down figure (blob + head dot + drop shadow)
// with a two-backend animation state machine (procedural tweens now; atlas in Plan 09-03).
//
// D-01 placeholder look: drop shadow oval → body ellipse → head dot circle, all inside
// an inner _figureContainer. The outer Container's position/rotation is owned by GameScene
// (setPosition + setRotation(-facing) — LOCKED sign). The inner _figureContainer is the
// exclusive target for all animation tweens (Pitfalls 3/7 from RESEARCH.md).
//
// D-04 two-backend design: AnimStateMachine decides current state; _applyProceduralState /
// _applyProceduralOneShot render it via tweens. Plan 09-03 adds the atlas backend;
// setAtlas() will flip to it without touching state logic.
//
// Phaser 4 ESM has NO default export (Pitfall 2).
// Source: Phase 09 CONTEXT.md D-01/D-02/D-04; 09-UI-SPEC.md §Placeholder Figure Visual Contract
//         + §Procedural Animation Parameters; 09-PATTERNS.md §Constructor shape.

import * as Phaser from 'phaser';
import { renderBus } from '../bus';
import { AnimStateMachine } from './anim-state-machine';
import type { AnimState } from './anim-state-machine';

export const CHARACTER_RADIUS = 8;
export const CHARACTER_FILL = 0xe8a838;   // UI-SPEC accent amber (default / hider)

/**
 * Per-role fill colour. Colours drawn from the UI-SPEC warm-chill palette.
 *   hider  → amber  (#e8a838) — the protagonist colour
 *   hunter → coral  (#ff7f66) — warm but distinct from hider amber
 *   dog    → tan    (#b88a4a) — muted; reads as "furry"
 *   adult  → sage   (#8fb074) — green-muted; reads as "background NPC"
 */
export type CharacterRole = 'hider' | 'hunter' | 'dog' | 'adult';
export const CHARACTER_ROLE_FILL: Record<CharacterRole, number> = {
  hider: 0xe8a838,
  hunter: 0xff7f66,
  dog: 0xb88a4a,
  adult: 0x8fb074,
};

/**
 * Per-state default frame rates for atlas animations (D-08 artist guidance).
 * These are recommendations published in docs/sprite-authoring.md; the artist
 * may deliver any number of frames and they will all be used at this fps.
 * Override in code if a specific character needs different timing.
 */
const DEFAULT_FRAME_RATE: Record<AnimState, number> = {
  idle:    4,
  walk:   10,
  sprint: 14,
  crouch:  6,
  lunge:  12,
  gesture: 5,
  afraid:  8,
};

export class CharacterSprite extends Phaser.GameObjects.Container {
  private _stateMachine: AnimStateMachine;
  private _figureContainer: Phaser.GameObjects.Container;
  private _busOff: Array<() => void> = [];
  private _entityId: string;
  /** Tracks the last applied visual state so we can detect crouch-exit transitions. */
  private _lastState: AnimState = 'idle';
  /** Internal Sprite added to _figureContainer when setAtlas() is called. */
  private _atlasSprite: Phaser.GameObjects.Sprite | null = null;
  /** True once setAtlas() has been called and the atlas backend is active. */
  private _hasAtlas = false;
  /** The atlas key passed to setAtlas() — stored so one-shot handler can reconstruct anim keys. */
  private _atlasKey: string | null = null;

  /**
   * @param scene    Phaser scene context
   * @param x        World-space X (set each frame by GameScene)
   * @param y        World-space Y (set each frame by GameScene)
   * @param role     Character role for per-role tint (D-02)
   * @param entityId Entity id for per-entity render-bus subscription filtering.
   *                 Defaults to '' so existing GameScene call sites compile before
   *                 Plan 09-04 threads real ids through.
   */
  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    role: CharacterRole = 'hider',
    entityId: string = '',
  ) {
    super(scene, x, y);

    this._entityId = entityId;
    this._stateMachine = new AnimStateMachine();

    // Inner container — ALL tweens target _figureContainer, NEVER the outer Container.
    // Outer Container x/y/rotation are owned by GameScene (LOCKED facing sign).
    // (Pitfall 3/7: tween on outer Container fights GameScene setPosition/setRotation.)
    this._figureContainer = scene.add.container(0, 0);

    const fill = CHARACTER_ROLE_FILL[role] ?? CHARACTER_FILL;
    // Head dot is 25% darker per UI-SPEC: multiply each RGB channel by 0.75.
    const headFill = Math.floor(fill * 0.75);

    // Child order: shadow (0, behind) → body (1) → head (2, in front).
    // First added = drawn first = rendered behind later children.
    const shadow = scene.add.ellipse(0, 4, 12, 4, 0x0d1f2d, 0.4);
    const body   = scene.add.ellipse(0, 0, 10, 8, fill);
    const head   = scene.add.circle(8, 0, 4, headFill);

    this._figureContainer.add([shadow, body, head]);
    this.add(this._figureContainer);
    scene.add.existing(this);

    // ── Per-entity renderBus subscriptions ──────────────────────────────────
    // Each subscription filters by the entity's own id.
    // Off-functions collected in _busOff for teardown in destroy().
    // Field names are CONFIRMED from bus.ts — NOT ev.name, but ev.threshold for fear.
    this._busOff.push(
      // ONLY the catcher (ev.hunterId) plays lunge; the caught hider (ev.hiderId) must NOT.
      renderBus.on('catch', (ev) => {
        if (ev.hunterId === this._entityId) this.triggerOneShot('lunge');
      }),
      // ONLY the liberator (ev.liberatorId) plays gesture; freedIds do NOT get their own one-shot.
      renderBus.on('hider-released', (ev) => {
        if (ev.liberatorId === this._entityId) this.triggerOneShot('gesture');
      }),
      // Field is ev.threshold (NOT ev.name) — confirmed bus.ts L214-219.
      renderBus.on('fear-threshold-crossed', (ev) => {
        if (ev.entityId === this._entityId) {
          this._stateMachine.setAfraid(ev.threshold, ev.direction);
        }
      }),
      renderBus.on('hiding-entered', (ev) => {
        if (ev.entityId === this._entityId) this._stateMachine.setHiding(true);
      }),
      renderBus.on('hiding-exited', (ev) => {
        if (ev.entityId === this._entityId) this._stateMachine.setHiding(false);
      }),
    );

    // Start with idle breathe so the figure animates immediately on construction.
    this._lastState = 'idle';
    this._applyProceduralState('idle');
  }

  // ── Destroy ─────────────────────────────────────────────────────────────────

  destroy(fromScene?: boolean): void {
    // Release all render-bus subscriptions before tearing down (manager.ts pattern).
    for (const off of this._busOff) off();
    this._busOff = [];
    super.destroy(fromScene);
  }

  // ── Public animation API (called by GameScene per-frame) ─────────────────────

  /**
   * Drive the state machine from current entity snapshot. Called each frame
   * from GameScene's entity update loop (Plan 09-04).
   *
   * @param entity     Entity snapshot (status + fear are the relevant fields)
   * @param isMoving   True when entity has non-zero velocity this frame
   * @param isSprinting True when entity intent includes sprint
   */
  updateAnimState(
    entity: { status: string; fear: number },
    isMoving: boolean,
    isSprinting: boolean,
  ): void {
    // Keep hiding flag in sync from entity status rather than solely from bus
    // events (bus events may arrive out of order on first frame).
    this._stateMachine.setHiding(entity.status === 'hiding');

    const newState = this._stateMachine.update({ isMoving, isSprinting });
    if (newState !== null) {
      // If we were in crouch and are now leaving it, tween scale back smoothly.
      if (this._lastState === 'crouch' && newState !== 'crouch') {
        this._exitCrouch();
      } else {
        this._applyProceduralState(newState);
      }
      this._lastState = newState;
    }
  }

  /**
   * Trigger a non-interruptible one-shot (lunge / gesture).
   * No-op if the machine is already latched (D-06: no re-trigger mid-play).
   * Dispatches to the atlas backend when setAtlas() has been called (SPRITE-05).
   */
  triggerOneShot(state: 'lunge' | 'gesture'): void {
    const duration = this._stateMachine.latchOneShot(state);
    if (duration === 0) return; // already latched
    if (this._hasAtlas) {
      this._applyAtlasOneShot(state);
    } else {
      this._applyProceduralOneShot(state);
    }
  }

  // ── Procedural animation backend ─────────────────────────────────────────────

  /**
   * Apply a looping procedural state tween to _figureContainer.
   *
   * THREAT T-09-02-01 mitigation: kills all existing tweens on _figureContainer
   * first — prevents unbounded tween accumulation on rapid state churn.
   */
  private _applyProceduralState(state: AnimState): void {
    // Kill ALL tweens on _figureContainer before starting new ones (RESEARCH Pitfall 3;
    // stacked tweens combine multiplicatively and produce nonsensical motion).
    this.scene.tweens.killTweensOf(this._figureContainer);

    switch (state) {
      case 'idle':
        // Gentle breathe: scale 1.0 ↔ 1.03, 2000 ms period (yoyo doubles duration).
        this.scene.tweens.add({
          targets: this._figureContainer,
          scaleX: 1.03,
          scaleY: 1.03,
          duration: 1000,       // half-period; yoyo makes full period 2000 ms
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
        break;

      case 'walk':
        // Vertical bob -2 px, 167 ms period (~6 bobs/s).
        this.scene.tweens.add({
          targets: this._figureContainer,
          y: -2,
          duration: 83,         // half-period; yoyo → 167 ms
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
        break;

      case 'sprint':
        // Bigger/faster bob -4 px, 111 ms period (~9 bobs/s).
        this.scene.tweens.add({
          targets: this._figureContainer,
          y: -4,
          duration: 56,         // half-period; yoyo → 111 ms
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
        break;

      case 'crouch':
        // Instant squash on enter — sudden hide feel (no tween on entry).
        // THREAT T-09-02-02: applied to _figureContainer only, not outer Container.
        this._figureContainer.setScale(1.1, 0.7);
        break;

      case 'afraid':
        // Jitter: random ±2 px X and Y at ~24 Hz.
        // THREAT T-09-02-02: applied to _figureContainer only; re-zeroed on exit
        // so outer Container position (GameScene-authoritative) is never mutated.
        this.scene.tweens.add({
          targets: this._figureContainer,
          x: 2,                 // tween target drives update callback; value is arbitrary
          duration: 42,         // ~24 Hz update rate
          repeat: -1,
          yoyo: true,
          onUpdate: () => {
            this._figureContainer.x = (Math.random() - 0.5) * 4; // ±2 px
            this._figureContainer.y = (Math.random() - 0.5) * 4;
          },
          onStop: () => {
            // Re-zero on state exit so position drift cannot accumulate.
            this._figureContainer.x = 0;
            this._figureContainer.y = 0;
          },
        });
        break;

      case 'lunge':
      case 'gesture':
        // One-shots are only applied via _applyProceduralOneShot; reaching here
        // after releaseOneShot() + re-resolve is harmless — fall through to idle.
        this._applyProceduralState('idle');
        break;

      default:
        // Exhaustiveness guard — should never reach this.
        break;
    }
  }

  /**
   * Exit crouch: tween scale back to (1, 1) over 150 ms ease-out.
   * Called when state transitions away from crouch.
   */
  private _exitCrouch(): void {
    this.scene.tweens.killTweensOf(this._figureContainer);
    this.scene.tweens.add({
      targets: this._figureContainer,
      scaleX: 1.0,
      scaleY: 1.0,
      duration: 150,
      ease: 'Cubic.easeOut',
    });
  }

  /**
   * Play a non-interruptible one-shot procedural animation.
   * On completion: releases the latch and re-applies the re-resolved state.
   *
   * lunge:   150 ms stretch (scaleX→1.25, scaleY→0.8 ease-in) +
   *          300 ms return  (scaleX→1.0,  scaleY→1.0 ease-out) = 450 ms total
   * gesture: angle ±11.5 deg (≈±0.2 rad), 3 full cycles, 1000 ms total
   */
  private _applyProceduralOneShot(state: 'lunge' | 'gesture'): void {
    this.scene.tweens.killTweensOf(this._figureContainer);

    if (state === 'lunge') {
      // 150 ms stretch then chained 300 ms return = 450 ms total (UI-SPEC).
      this.scene.tweens.add({
        targets: this._figureContainer,
        scaleX: 1.25,
        scaleY: 0.8,
        duration: 150,
        ease: 'Cubic.easeIn',
        onComplete: () => {
          this.scene.tweens.add({
            targets: this._figureContainer,
            scaleX: 1.0,
            scaleY: 1.0,
            duration: 300,
            ease: 'Cubic.easeOut',
            onComplete: () => {
              this._stateMachine.releaseOneShot();
              this._applyProceduralState(this._stateMachine.state);
            },
          });
        },
      });
    } else {
      // gesture: angle ±11.5 deg, 3 cycles × 333 ms = 1000 ms (UI-SPEC).
      // Implementation: 167 ms half-period × 6 half-periods (yoyo repeat=5) = 1002 ms ≈ 1000 ms.
      this.scene.tweens.add({
        targets: this._figureContainer,
        angle: 11.5,           // ≈ 0.2 rad
        duration: 167,         // half-period
        yoyo: true,
        repeat: 5,             // 6 half-periods = 3 full cycles
        ease: 'Sine.easeInOut',
        onComplete: () => {
          this._figureContainer.angle = 0;
          this._stateMachine.releaseOneShot();
          this._applyProceduralState(this._stateMachine.state);
        },
      });
    }
  }

  // ── Atlas backend (D-04 second backend, D-08 data-driven frame discovery) ────

  /**
   * Swap to the atlas rendering backend.
   *
   * Removes all procedural Graphics children from _figureContainer, creates one
   * Phaser.GameObjects.Sprite bound to the atlas, and registers per-state animations
   * using DATA-DRIVEN frame discovery (D-08):
   *   - getFrameNames() on the loaded texture — no hardcoded start/end counts required
   *   - Frames filtered by `${atlasKey}-${state}-` prefix then sorted by trailing integer index
   *   - States with zero frames log a dev-only console.warn and are skipped (no throw)
   *   - Key collision guarded: `anims.exists(key)` → `anims.remove(key)` before `anims.create`
   *     so re-registering the same characterId is safe (Pitfall 5 from RESEARCH.md)
   *
   * Animation repeat flags (UI-SPEC):
   *   - lunge / gesture: repeat=0 (play-once)
   *   - all other states: repeat=-1 (loop)
   *
   * State logic is untouched — this only swaps the rendering backend.
   */
  setAtlas(atlasKey: string, _frameKey: string): void {
    // Kill all procedural tweens and clear the figure children.
    this.scene.tweens.killTweensOf(this._figureContainer);
    this._figureContainer.removeAll(true);

    // Discover and register per-state animations (D-08 data-driven — getFrameNames, not fixed counts).
    const states: AnimState[] = ['idle', 'walk', 'sprint', 'crouch', 'lunge', 'gesture', 'afraid'];
    const allFrameNames: string[] = this.scene.textures.get(atlasKey).getFrameNames();

    for (const state of states) {
      const prefix = `${atlasKey}-${state}-`;
      const frames = allFrameNames
        .filter((f) => f.startsWith(prefix))
        .sort((a, b) => parseInt(a.slice(prefix.length), 10) - parseInt(b.slice(prefix.length), 10));

      if (frames.length === 0) {
        // Dev-only warning: artist hasn't delivered this state yet. Never throw.
        console.warn(`CharacterSprite: atlas "${atlasKey}" has no frames for state "${state}"`);
        continue;
      }

      const animKey = `${atlasKey}-${state}`;
      // Guard against key collision (Pitfall 5): remove before re-registering.
      if (this.scene.anims.exists(animKey)) {
        this.scene.anims.remove(animKey);
      }
      const isOneShot = state === 'lunge' || state === 'gesture';
      this.scene.anims.create({
        key: animKey,
        frames: frames.map((frame) => ({ key: atlasKey, frame })),
        frameRate: DEFAULT_FRAME_RATE[state],
        repeat: isOneShot ? 0 : -1,   // 0 = play-once, -1 = loop (UI-SPEC)
      });
    }

    // Create the internal Sprite, add to _figureContainer, and start idle.
    this._atlasSprite = this.scene.add.sprite(0, 0, atlasKey);
    this._figureContainer.add(this._atlasSprite);
    this._atlasKey = atlasKey;
    this._hasAtlas = true;
    this._atlasSprite.anims.play(`${atlasKey}-idle`);
  }

  /**
   * Play an atlas animation on the internal sprite.
   * No-op if setAtlas() has not been called yet (procedural backend is active).
   */
  playAnim(animKey: string): void {
    if (!this._hasAtlas || !this._atlasSprite) return;
    this._atlasSprite.anims.play(animKey);
  }

  /**
   * Atlas one-shot backend (SPRITE-05).
   * Plays the one-shot animation on the internal sprite; on `animationcomplete`
   * releases the latch and re-applies the current resolved state.
   */
  private _applyAtlasOneShot(state: 'lunge' | 'gesture'): void {
    if (!this._atlasSprite || !this._atlasKey) return;
    const animKey = `${this._atlasKey}-${state}`;
    this._atlasSprite.anims.play(animKey);
    this._atlasSprite.once('animationcomplete', () => {
      this._stateMachine.releaseOneShot();
      // Re-resolve current state and resume the loop animation.
      const nextState = this._stateMachine.state;
      if (this._atlasSprite && this._atlasKey) {
        this._atlasSprite.anims.play(`${this._atlasKey}-${nextState}`);
      }
    });
  }

  // ── Physics body ─────────────────────────────────────────────────────────────

  /**
   * Configure the Arcade body attached by `scene.physics.add.existing(this)`
   * to match the visual 8-px circle. Must be called AFTER
   * `scene.physics.add.existing` — the body does not exist before that.
   *
   * TWO Phaser-4 Container gotchas are addressed here; miss either and the
   * character walks through walls despite the collider being wired.
   *
   * (1) DEFAULT BODY SIZE: Phaser 4's default body for a Container is a
   *     1×1 AABB at origin — too small to produce reliable separations
   *     against 32×32 wall tiles. `setCircle(CHARACTER_RADIUS)` fixes the size.
   *
   * (2) BODY POSITION vs VISUAL POSITION: With default offsets, setCircle
   *     places the body box with its TOP-LEFT at the Container origin, making
   *     the body CENTRE offset by (+r, +r) from the visual circle centre.
   *     Passing explicit `offsetX = -r`, `offsetY = -r` centres the body on
   *     the Container origin so body centre and visual centre coincide.
   *
   * Throws (fail-loud) if called before physics.add.existing.
   */
  configurePhysicsBody(): void {
    const body = this.body as Phaser.Physics.Arcade.Body | undefined;
    if (!body) {
      throw new Error(
        'CharacterSprite.configurePhysicsBody: body is undefined — call scene.physics.add.existing(this) first',
      );
    }
    body.setCircle(CHARACTER_RADIUS, -CHARACTER_RADIUS, -CHARACTER_RADIUS);
  }
}
